import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'System|Criteria|Rules|Form|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.Core,
	portal: 'SP',
};

type LocNameStart = 'SPSystemCriteriaRuleForm';
export type SPSystemCriteriaRuleFormLocNames =
	| `${LocNameStart}LabelName`
	| `${LocNameStart}TitleTable`
	| `${LocNameStart}LabelRule`
	| `${LocNameStart}LabelRegion`
	| `${LocNameStart}ContentNone`
	| `${LocNameStart}LabelMinItemsRequired`
	| `${LocNameStart}ErrorMinItemsRequiredRequired`
	| `${LocNameStart}ErrorMinItemsRequiredNumber`
	| `${LocNameStart}ErrorMinItemsRequiredZero`
	| `${LocNameStart}LabelRequireAllItems`
	| `${LocNameStart}LabelNot`
	| `${LocNameStart}ErrorMinItemsRequiredLessThanItemsNumber`
	| `${LocNameStart}ErrorRequiredItem`;

export const spSystemCriteriaRuleFormLocs: LocDefs<SPSystemCriteriaRuleFormLocNames> = {
	SPSystemCriteriaRuleFormLabelName: {
		defaultValue: 'Name',
		description: 'Column heading for: Name.',
		displayPath: `${pathStart}Name`,
		displayType: 'Label',
		...sharedProperties,
	},
	SPSystemCriteriaRuleFormTitleTable: {
		defaultValue: 'Rule Items',
		description: 'Table title.',
		displayPath: `${pathStart}Table Title`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPSystemCriteriaRuleFormLabelRule: {
		defaultValue: 'Rule',
		description: 'Label for: Rule.',
		displayPath: `${pathStart}Rule`,
		displayType: 'Label',
		legacyName: 'Announcements/Tools.AnnouncementCriteria.FieldLabel.Rule',
		...sharedProperties,
	},
	SPSystemCriteriaRuleFormLabelRegion: {
		defaultValue: 'Region',
		description: 'Label for: Region.',
		displayPath: `${pathStart}Region`,
		displayType: 'Label',
		legacyName: 'Announcements/Tools.AnnouncementCriteria.FieldLabel.Region',
		...sharedProperties,
	},
	SPSystemCriteriaRuleFormContentNone: {
		defaultValue: 'None',
		description: 'Region selection for none.',
		displayPath: `${pathStart}None`,
		displayType: 'Content',
		legacyName: 'Announcements/Tools.AnnouncementCriteria.MenuItem.None',
		...sharedProperties,
	},
	SPSystemCriteriaRuleFormLabelMinItemsRequired: {
		defaultValue: 'Min Items Required',
		description: 'Label for: Min Items Required.',
		displayPath: `${pathStart}Min Items Required`,
		displayType: 'Label',
		legacyName: 'Announcements/Tools.AnnouncementCriteria.FieldLabel.ItemsRequired',
		...sharedProperties,
	},
	SPSystemCriteriaRuleFormErrorMinItemsRequiredRequired: {
		defaultValue: 'Min Items Required is required',
		description: 'Error for Min Items Required is required.',
		displayPath: `${pathStart}Min Items Required is Required`,
		displayType: 'Error',
		legacyName: 'Announcements/Tools.AnnouncementCriteria.FieldError.ItemsRequiredRequired',
		...sharedProperties,
	},
	SPSystemCriteriaRuleFormErrorMinItemsRequiredNumber: {
		defaultValue: 'Min Items Required must only contain numbers and not start with a zero',
		description: 'Error for Min Items Required number.',
		displayPath: `${pathStart}Min Items Required Number`,
		displayType: 'Error',
		legacyName: 'Announcements/Tools.AnnouncementCriteria.FieldError.ItemsRequiredNumber',
		...sharedProperties,
	},
	SPSystemCriteriaRuleFormErrorMinItemsRequiredZero: {
		defaultValue: 'Min Items Required must be greater than 0',
		description: 'Error for Min Items Required must be greater than 0.',
		displayPath: `${pathStart}Min Items Required Zerio`,
		displayType: 'Error',
		legacyName: 'Announcements/Tools.AnnouncementCriteria.FieldError.ItemsRequiredMin',
		...sharedProperties,
	},
	SPSystemCriteriaRuleFormLabelRequireAllItems: {
		defaultValue: 'Require All Items',
		description: 'Label for: Require All Items.',
		displayPath: `${pathStart}Require All Items`,
		displayType: 'Label',
		legacyName: 'Announcements/Tools.AnnouncementCriteria.FieldLabel.RequireAllItems',
		...sharedProperties,
	},
	SPSystemCriteriaRuleFormLabelNot: {
		defaultValue: 'Not',
		description: 'Label for: Not.',
		displayPath: `${pathStart}Not`,
		displayType: 'Label',
		legacyName: 'Announcements/Tools.AnnouncementCriteria.FieldLabel.Not',
		...sharedProperties,
	},
	SPSystemCriteriaRuleFormErrorMinItemsRequiredLessThanItemsNumber: {
		defaultValue:
			'Min Items Required must be less than or equal to the number of selected items',
		description: 'Error for: Min Items Required and items selected.',
		displayPath: `${pathStart}Min Items Required and items selected`,
		displayType: 'Error',
		legacyName:
			'Announcements/Tools.AnnouncementCriteria.FieldError.ItemsRequiredLessThanItemsNum',
		...sharedProperties,
	},
	SPSystemCriteriaRuleFormErrorRequiredItem: {
		defaultValue: 'At least one item is required',
		description: 'Error for: At least one item is required.',
		displayPath: `${pathStart}At least one item is required`,
		displayType: 'Error',
		legacyName: 'Announcements/Tools.AnnouncementCriteria.FieldError.RequiredItem',
		...sharedProperties,
	},
};

export type SPSystemCriteriaRuleFormLocType = LocMap<SPSystemCriteriaRuleFormLocNames>;
